import React, { useState, useEffect } from 'react';
import { ArrowUpCircle, ArrowDownCircle, RefreshCw, Search, DollarSign, TrendingUp, CreditCard, ShoppingCart, Download, Repeat, AlertCircle, CheckCircle } from 'lucide-react';
import { COLORS } from '../../../../utils/constants';
import { adminAPI } from '../../../../services/api';

const BalanceHistoryTab = ({ clientId }) => {
  const [history, setHistory] = useState([]);
  const [loading, setLoading] = useState(true);
  const [filter, setFilter] = useState('all');
  const [searchQuery, setSearchQuery] = useState('');
  const [days, setDays] = useState(90);

  useEffect(() => {
    loadHistory();
  }, [clientId, days]);

  const loadHistory = async () => {
    setLoading(true);
    try {
      const res = await adminAPI.getClientBalanceHistory(clientId, days);
      setHistory(res.data?.history || []);
    } catch (err) {
      console.error('Failed to load balance history:', err);
      setHistory([]);
    } finally {
      setLoading(false);
    }
  };

  const getTypeInfo = (type) => {
    const types = {
      // Депозиты и выводы
      'deposit': { label: 'Депозит', icon: ArrowDownCircle, color: COLORS.success, prefix: '+' },
      'withdrawal': { label: 'Вывод', icon: ArrowUpCircle, color: COLORS.danger, prefix: '-' },
      
      // Начисления и списания (админ)
      'add': { label: 'Начисление', icon: ArrowDownCircle, color: COLORS.success, prefix: '+' },
      'subtract': { label: 'Списание', icon: ArrowUpCircle, color: COLORS.danger, prefix: '-' },
      'adjustment': { label: 'Коррекция', icon: Repeat, color: COLORS.primary, prefix: '' },
      'set': { label: 'Установка баланса', icon: DollarSign, color: COLORS.primary, prefix: '' },
      
      // Торговля
      'trade_profit': { label: 'Прибыль (торговля)', icon: TrendingUp, color: COLORS.success, prefix: '+' },
      'trade_loss': { label: 'Убыток (торговля)', icon: TrendingUp, color: COLORS.danger, prefix: '-' },
      'position_open': { label: 'Открытие позиции', icon: TrendingUp, color: '#3b82f6', prefix: '-' },
      'position_close': { label: 'Закрытие позиции', icon: TrendingUp, color: COLORS.success, prefix: '+' },
      'margin': { label: 'Маржа', icon: CreditCard, color: '#f59e0b', prefix: '-' },
      
      // Спот
      'spot_buy': { label: 'Покупка Spot', icon: ShoppingCart, color: '#3b82f6', prefix: '-' },
      'spot_sell': { label: 'Продажа Spot', icon: ShoppingCart, color: COLORS.success, prefix: '+' },
      'spot_add': { label: 'Начисление Spot', icon: ArrowDownCircle, color: COLORS.success, prefix: '+' },
      'spot_subtract': { label: 'Списание Spot', icon: ArrowUpCircle, color: COLORS.danger, prefix: '-' },
      
      // Комиссии и возвраты
      'commission': { label: 'Комиссия', icon: CreditCard, color: COLORS.textSecondary, prefix: '-' },
      'fee': { label: 'Сбор', icon: CreditCard, color: COLORS.textSecondary, prefix: '-' },
      'refund': { label: 'Возврат', icon: Repeat, color: '#06b6d4', prefix: '+' },
      
      // IDO
      'ido_purchase': { label: 'Покупка IDO', icon: ShoppingCart, color: '#8b5cf6', prefix: '-' },
      'ido_refund': { label: 'Возврат IDO', icon: Repeat, color: '#06b6d4', prefix: '+' },
      
      // Системные
      'system': { label: 'Системная операция', icon: AlertCircle, color: COLORS.textSecondary, prefix: '' },
      'transfer_in': { label: 'Входящий перевод', icon: ArrowDownCircle, color: COLORS.success, prefix: '+' },
      'transfer_out': { label: 'Исходящий перевод', icon: ArrowUpCircle, color: COLORS.danger, prefix: '-' },
    };
    return types[type] || { label: type || 'Операция', icon: DollarSign, color: COLORS.textSecondary, prefix: '' };
  };

  const filterTypes = [
    { id: 'all', label: 'Все' },
    { id: 'deposit', label: 'Депозиты' },
    { id: 'withdrawal', label: 'Выводы' },
    { id: 'add', label: 'Начисления' },
    { id: 'subtract', label: 'Списания' },
    { id: 'trade', label: 'Торговля' },
    { id: 'spot', label: 'Spot' },
    { id: 'ido', label: 'IDO' },
  ];

  const filteredHistory = history.filter(item => {
    // Filter by type
    if (filter !== 'all') {
      const itemType = item.type || '';
      if (filter === 'trade') {
        if (!itemType.includes('trade') && !itemType.includes('position') && !itemType.includes('margin')) return false;
      } else if (filter === 'spot') {
        if (!itemType.includes('spot')) return false;
      } else if (filter === 'ido') {
        if (!itemType.includes('ido')) return false;
      } else if (itemType !== filter) {
        return false;
      }
    }
    
    // Filter by search
    if (searchQuery) {
      const query = searchQuery.toLowerCase();
      return (
        item.notes?.toLowerCase().includes(query) ||
        item.type?.toLowerCase().includes(query) ||
        item.amount?.toString().includes(query)
      );
    }
    return true;
  });

  // Calculate totals
  const totals = history.reduce((acc, item) => {
    const amount = Math.abs(item.amount || 0);
    const type = item.type || '';
    
    // Income types
    if (['deposit', 'add', 'refund', 'trade_profit', 'spot_sell', 'position_close', 'transfer_in', 'ido_refund'].includes(type)) {
      acc.incoming += amount;
    } 
    // Expense types
    else if (['withdrawal', 'subtract', 'commission', 'fee', 'trade_loss', 'spot_buy', 'position_open', 'margin', 'transfer_out', 'ido_purchase'].includes(type)) {
      acc.outgoing += amount;
    }
    return acc;
  }, { incoming: 0, outgoing: 0 });

  // Export to CSV
  const exportToCSV = () => {
    const headers = ['Дата', 'Тип', 'Сумма', 'Баланс после', 'Примечание'];
    const rows = filteredHistory.map(item => {
      const typeInfo = getTypeInfo(item.type);
      return [
        item.created_at ? new Date(item.created_at).toLocaleString('ru-RU') : '',
        typeInfo.label,
        `${typeInfo.prefix}$${Math.abs(item.amount || 0).toFixed(2)}`,
        item.balance_after !== undefined ? `$${item.balance_after.toFixed(2)}` : '',
        item.notes || ''
      ];
    });
    
    const csvContent = [headers, ...rows].map(row => row.join(',')).join('\n');
    const blob = new Blob([csvContent], { type: 'text/csv;charset=utf-8;' });
    const link = document.createElement('a');
    link.href = URL.createObjectURL(blob);
    link.download = `balance_history_${clientId}.csv`;
    link.click();
  };

  if (loading) {
    return (
      <div style={{ textAlign: 'center', padding: '40px', color: COLORS.textSecondary }}>
        <RefreshCw size={24} className="animate-spin" style={{ marginBottom: '12px' }} />
        <div>Загрузка истории...</div>
      </div>
    );
  }

  return (
    <div>
      {/* Summary */}
      <div style={{ display: 'grid', gridTemplateColumns: 'repeat(4, 1fr)', gap: '12px', marginBottom: '20px' }}>
        <div style={{ padding: '14px', backgroundColor: COLORS.background, borderRadius: '10px', textAlign: 'center' }}>
          <div style={{ color: COLORS.textSecondary, fontSize: '11px', marginBottom: '4px' }}>Всего поступлений</div>
          <div style={{ color: COLORS.success, fontSize: '18px', fontWeight: '700' }}>+${totals.incoming.toFixed(2)}</div>
        </div>
        <div style={{ padding: '14px', backgroundColor: COLORS.background, borderRadius: '10px', textAlign: 'center' }}>
          <div style={{ color: COLORS.textSecondary, fontSize: '11px', marginBottom: '4px' }}>Всего списаний</div>
          <div style={{ color: COLORS.danger, fontSize: '18px', fontWeight: '700' }}>-${totals.outgoing.toFixed(2)}</div>
        </div>
        <div style={{ padding: '14px', backgroundColor: COLORS.background, borderRadius: '10px', textAlign: 'center' }}>
          <div style={{ color: COLORS.textSecondary, fontSize: '11px', marginBottom: '4px' }}>Разница</div>
          <div style={{ color: totals.incoming - totals.outgoing >= 0 ? COLORS.success : COLORS.danger, fontSize: '18px', fontWeight: '700' }}>
            {totals.incoming - totals.outgoing >= 0 ? '+' : ''}{(totals.incoming - totals.outgoing).toFixed(2)}$
          </div>
        </div>
        <div style={{ padding: '14px', backgroundColor: COLORS.background, borderRadius: '10px', textAlign: 'center' }}>
          <div style={{ color: COLORS.textSecondary, fontSize: '11px', marginBottom: '4px' }}>Операций</div>
          <div style={{ color: COLORS.text, fontSize: '18px', fontWeight: '700' }}>{history.length}</div>
        </div>
      </div>

      {/* Period Selection */}
      <div style={{ display: 'flex', gap: '8px', marginBottom: '16px', alignItems: 'center' }}>
        <span style={{ color: COLORS.textSecondary, fontSize: '12px' }}>Период:</span>
        {[30, 60, 90, 180, 365].map(d => (
          <button
            key={d}
            onClick={() => setDays(d)}
            style={{
              padding: '6px 12px',
              backgroundColor: days === d ? COLORS.primary : COLORS.background,
              border: 'none',
              borderRadius: '6px',
              color: days === d ? '#000' : COLORS.textSecondary,
              fontSize: '12px',
              fontWeight: days === d ? '600' : '400',
              cursor: 'pointer',
            }}
          >
            {d}д
          </button>
        ))}
        <button
          onClick={exportToCSV}
          style={{
            marginLeft: 'auto',
            padding: '6px 12px',
            backgroundColor: COLORS.background,
            border: `1px solid ${COLORS.border}`,
            borderRadius: '6px',
            color: COLORS.textSecondary,
            fontSize: '12px',
            cursor: 'pointer',
            display: 'flex',
            alignItems: 'center',
            gap: '4px',
          }}
        >
          <Download size={14} />
          Экспорт
        </button>
      </div>

      {/* Filters */}
      <div style={{ display: 'flex', gap: '8px', marginBottom: '16px', flexWrap: 'wrap' }}>
        {filterTypes.map(f => (
          <button
            key={f.id}
            onClick={() => setFilter(f.id)}
            style={{
              padding: '8px 14px',
              backgroundColor: filter === f.id ? COLORS.primary : COLORS.background,
              border: 'none',
              borderRadius: '20px',
              color: filter === f.id ? '#000' : COLORS.textSecondary,
              fontSize: '12px',
              fontWeight: '600',
              cursor: 'pointer',
            }}
          >
            {f.label}
          </button>
        ))}
      </div>

      {/* Search */}
      <div style={{ position: 'relative', marginBottom: '16px' }}>
        <Search size={16} style={{ position: 'absolute', left: '12px', top: '50%', transform: 'translateY(-50%)', color: COLORS.textSecondary }} />
        <input
          type="text"
          value={searchQuery}
          onChange={(e) => setSearchQuery(e.target.value)}
          placeholder="Поиск по истории..."
          style={{
            width: '100%',
            padding: '10px 12px 10px 36px',
            backgroundColor: COLORS.background,
            border: `1px solid ${COLORS.border}`,
            borderRadius: '8px',
            color: COLORS.text,
            fontSize: '13px',
            boxSizing: 'border-box',
          }}
        />
      </div>

      {/* History List */}
      {filteredHistory.length > 0 ? (
        <div style={{ display: 'flex', flexDirection: 'column', gap: '8px' }}>
          {filteredHistory.map((item, idx) => {
            const typeInfo = getTypeInfo(item.type);
            const Icon = typeInfo.icon;
            return (
              <div key={item.id || idx} style={{
                padding: '14px',
                backgroundColor: COLORS.card,
                borderRadius: '10px',
                border: `1px solid ${COLORS.border}`,
                display: 'flex',
                alignItems: 'center',
                gap: '12px',
              }}>
                <div style={{
                  width: '40px',
                  height: '40px',
                  borderRadius: '10px',
                  backgroundColor: `${typeInfo.color}15`,
                  display: 'flex',
                  alignItems: 'center',
                  justifyContent: 'center',
                  flexShrink: 0,
                }}>
                  <Icon size={20} color={typeInfo.color} />
                </div>
                
                <div style={{ flex: 1, minWidth: 0 }}>
                  <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: '4px' }}>
                    <span style={{ color: COLORS.text, fontSize: '14px', fontWeight: '600' }}>{typeInfo.label}</span>
                    <span style={{ color: typeInfo.color, fontSize: '16px', fontWeight: '700' }}>
                      {typeInfo.prefix}${Math.abs(item.amount || 0).toFixed(2)}
                    </span>
                  </div>
                  <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
                    <span style={{ color: COLORS.textSecondary, fontSize: '12px', overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap', maxWidth: '60%' }}>
                      {item.notes || '—'}
                    </span>
                    <span style={{ color: COLORS.textSecondary, fontSize: '11px' }}>
                      {item.created_at ? new Date(item.created_at).toLocaleString('ru-RU') : ''}
                    </span>
                  </div>
                  {item.balance_after !== undefined && item.balance_after !== null && (
                    <div style={{ color: COLORS.textSecondary, fontSize: '11px', marginTop: '4px' }}>
                      Баланс после: <span style={{ color: COLORS.text, fontWeight: '500' }}>${item.balance_after?.toFixed(2)}</span>
                    </div>
                  )}
                </div>
              </div>
            );
          })}
        </div>
      ) : (
        <div style={{ textAlign: 'center', padding: '40px', color: COLORS.textSecondary }}>
          <DollarSign size={32} style={{ marginBottom: '12px', opacity: 0.5 }} />
          <div>История пуста</div>
          <div style={{ fontSize: '12px', marginTop: '4px' }}>
            {searchQuery || filter !== 'all' ? 'Попробуйте изменить фильтры' : 'Нет операций за выбранный период'}
          </div>
        </div>
      )}
    </div>
  );
};

export default BalanceHistoryTab;
